import { useMemo } from 'react';
import useShots from '../contexts/shots/provider';
import { FEED_TYPES } from '../stores/reducers/shots';
import { RoomStates } from '../types/enums';

interface UseAdjustedVolumeParams {
	baseVolume: number;
	onsiteLocationId?: number;
	isSpotlightFeed: boolean;
	showingHoldSlide: boolean;
	embed: boolean;
	roomState: RoomStates;
	backstageVolume: number;
	isActive?: boolean;
	clearShot?: boolean;
}

/**
 * Volume gating shared by the SRT + WHIP onsite location streams: silent when
 * staged offscreen in spotlight, on the hold slide, inactive, or while the shot
 * is cleared; full base volume when embedded or editing; otherwise scaled by the
 * backstage volume.
 */
export default function useAdjustedVolume({
	baseVolume,
	onsiteLocationId,
	isSpotlightFeed,
	showingHoldSlide,
	embed,
	roomState,
	backstageVolume,
	isActive = true,
	clearShot = false,
}: UseAdjustedVolumeParams) {
	const { currentShot } = useShots();
	const shotFeeds = currentShot?.shot_data?.shotFeeds || [];

	// silent unless this location is in the live shot — spotlight stages the next shot early
	const isOffscreenSpotlight = useMemo(() => {
		const inCanvasShot = shotFeeds.some(
			(f) => f.feedType === FEED_TYPES.ONSITE_LOCATION && f.onsiteLocationId === onsiteLocationId,
		);
		return !inCanvasShot && isSpotlightFeed;
	}, [shotFeeds, onsiteLocationId, isSpotlightFeed]);

	return useMemo(() => {
		if (isOffscreenSpotlight || showingHoldSlide || !isActive || clearShot) return 0;
		if (embed || roomState === RoomStates.showbuilder) return baseVolume;
		return baseVolume * backstageVolume;
	}, [
		isOffscreenSpotlight,
		showingHoldSlide,
		isActive,
		clearShot,
		embed,
		roomState,
		baseVolume,
		backstageVolume,
	]);
}
